<script setup lang="ts">
import { computed } from "vue";
import { useRouteQuery } from "@vueuse/router";
import { Table, TableHeader, TableRow, TableHead, TableBody } from "@/components/ui/table";
import TablePagination from "@/components/ui/table/TablePagination.vue";
import TableSkeleton from "@/components/ui/table/TableSkeleton.vue";
import TicketTableRow from "./TicketTableRow.vue";
import { BAPICustomerSupportTicket } from "@/bapi-client/types/customer-support";

type Props = {
  customerId: string;
  tickets: BAPICustomerSupportTicket[];
  reportingGroups: Record<string, string>;
  totalTickets: number;
  isLoading: boolean;
};

const props = defineProps<Props>();
defineEmits(["refresh"]);

const sortColumn = useRouteQuery<string>("sort_by", "created_ts");
const sortDirection = useRouteQuery<"ASC" | "DESC">("sort_by_direction", "ASC");
const pageSize = useRouteQuery("page_size", "25", { transform: Number });
const pageNumber = useRouteQuery("page_number", "1", { transform: Number });
const totalPages = computed(() => (props.totalTickets === 0 ? 0 : Math.ceil(props.totalTickets / pageSize.value)));

const columns: Array<{ label: string; value: string }> = [
  { label: "Priority", value: "priority" },
  { label: "Ticket", value: "id" },
  { label: "Ticket Name", value: "name" },
  { label: "Reporting Group", value: "reporting_groups" },
  { label: "Asset(s)", value: "asset_ids" },
  { label: "RR", value: "railroads" },
  { label: "L/E", value: "loaded_empty" },
  { label: "Tag(s)", value: "tags" },
  { label: "Created", value: "created_ts" },
  { label: "Last Action", value: "updated_ts" },
  { label: "Status", value: "state" },
  { label: "Assignee", value: "assignees" },
];

const columnWidths = computed(() => {
  const widths: Record<string, string> = {
    priority: "w-20",
    id: "w-32",
    name: "w-72",
    reporting_groups: "w-52",
    asset_ids: "w-32",
    railroads: "w-16",
    loaded_empty: "w-16",
    tags: "w-40",
    created_ts: "w-32",
    updated_ts: "w-32",
    state: "w-32",
    assignees: "w-40",
  };

  return columns.map((column) => widths[column.value] ?? "w-32");
});

const skeletonColumns = computed(() => {
  const sizeMap: Record<string, "sm" | "md" | "lg"> = {
    priority: "sm",
    id: "md",
    name: "lg",
    reporting_groups: "lg",
    asset_ids: "md",
    railroads: "sm",
    loaded_empty: "sm",
    tags: "md",
    created_ts: "md",
    updated_ts: "md",
    state: "md",
  };

  return columns.map((column) => ({
    type:
      column.value === "assignees"
        ? ("avatar" as const)
        : column.value === "priority"
          ? ("checkbox" as const)
          : ("text" as const),
    size: (sizeMap[column.value] ?? "md") as "sm" | "md" | "lg",
  }));
});

function setSort(column: string) {
  if (column === sortColumn.value) {
    sortColumn.value = column;
    sortDirection.value = sortDirection.value === "ASC" ? "DESC" : "ASC";
    return sortDirection.value;
  }
  sortColumn.value = column;
  sortDirection.value = "ASC";
}

function handlePageChange(newPage: number) {
  pageNumber.value = newPage;
}

function handlePageSizeChange(newPageSize: number) {
  pageSize.value = newPageSize;
  if (props.totalTickets <= newPageSize) {
    pageNumber.value = 1;
  }
}
</script>

<template>
  <section class="border-border rounded-md border">
    <Table class="table-fixed overflow-x-auto">
      <TableHeader class="bg-muted border-border sticky top-0 z-20 border-t">
        <TableRow>
          <TableHead
            v-for="(column, index) in columns"
            :key="column.value"
            sortable
            :sorted="sortColumn === column.value ? (sortDirection === 'ASC' ? 'asc' : 'desc') : false"
            :on-sort="() => setSort(column.value)"
            :class="columnWidths[index]"
          >
            {{ column.label }}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableSkeleton v-if="isLoading" :columns="skeletonColumns" :rows="2" />
        <TicketTableRow
          v-for="ticket in tickets"
          v-else
          :key="ticket.id"
          :ticket="ticket"
          :customer-id="customerId"
          :reporting-groups="ticket.reporting_groups && ticket.reporting_groups.map((group) => reportingGroups[group])"
          @assigned="$emit('refresh')"
        />
      </TableBody>
    </Table>
    <TablePagination
      :server-pagination="{
        currentPage: pageNumber,
        pageSize: pageSize,
        totalCount: totalTickets,
        totalPages,
      }"
      @update:current-page="handlePageChange"
      @update:page-size="handlePageSizeChange"
    />
  </section>
</template>
